import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Building2, Pencil, Trash2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useState } from "react";

interface Startup {
  id: string;
  name: string;
  short_description: string;
  category: string;
  team_size: number;
  banner_url: string | null;
}

interface OwnedStartupsProps {
  startups: Startup[];
  onDelete: (id: string) => void;
}

export const OwnedStartups = ({ startups, onDelete }: OwnedStartupsProps) => {
  const { toast } = useToast();
  const [startupToDelete, setStartupToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async (id: string) => {
    try {
      console.log('Deleting startup:', id);
      setIsDeleting(true);
      
      const { error } = await supabase
        .from('startups')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting startup:', error);
        toast({
          title: "Error",
          description: "Failed to delete startup",
          variant: "destructive",
        });
        return;
      }

      console.log('Startup deleted successfully');
      onDelete(id);
      toast({
        title: "Success",
        description: "Startup deleted successfully",
      });
    } catch (error) {
      console.error('Error in handleDelete:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setStartupToDelete(null);
    }
  };

  if (!startups.length) {
    return (
      <Card className="bg-flux-black/50 border-flux-cyan/20">
        <CardContent className="p-6 text-center">
          <p className="text-gray-400">You haven't created any startups yet.</p>
          <Link to="/communities/startups/create">
            <Button className="mt-4 bg-flux-cyan hover:bg-flux-cyan/80">
              Create Your First Startup
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-flux-black/50 border-flux-cyan/20">
      <CardHeader>
        <CardTitle className="flex items-center text-flux-cyan">
          <Building2 className="w-5 h-5 mr-2" />
          My Startups
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {startups.map((startup) => (
            <Card key={startup.id} className="bg-black/30">
              <CardContent className="p-4">
                <div className="aspect-video mb-4 rounded-lg overflow-hidden">
                  {startup.banner_url ? (
                    <img 
                      src={startup.banner_url} 
                      alt={startup.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-flux-cyan/20 to-flux-cyan/5 flex items-center justify-center">
                      <Building2 className="w-12 h-12 text-flux-cyan/40" />
                    </div>
                  )}
                </div>
                <h3 className="font-semibold mb-2">{startup.name}</h3>
                <p className="text-sm text-gray-400 mb-4 line-clamp-2">
                  {startup.short_description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-flux-cyan bg-flux-cyan/10 px-2 py-1 rounded-full">
                    {startup.category}
                  </span>
                  <div className="flex gap-2">
                    <Link to={`/communities/startups/${startup.id}/edit`}>
                      <Button variant="outline" size="icon">
                        <Pencil className="w-4 h-4" />
                      </Button>
                    </Link>
                    <AlertDialog open={startupToDelete === startup.id} onOpenChange={(open) => !open && setStartupToDelete(null)}>
                      <AlertDialogTrigger asChild>
                        <Button 
                          variant="outline" 
                          size="icon"
                          className="border-red-500 text-red-500 hover:bg-red-500/10"
                          onClick={() => setStartupToDelete(startup.id)}
                          disabled={isDeleting}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This action cannot be undone. This will permanently delete your startup
                            and remove all associated data including roles, applications, and comments.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-red-500 hover:bg-red-600"
                            onClick={() => handleDelete(startup.id)}
                            disabled={isDeleting}
                          >
                            {isDeleting ? "Deleting..." : "Delete Startup"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};